import { useState } from "react";
import { TrendingUp, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const SEV_CLS = { high: "bg-red-50 text-red-600 border-red-200", info: "bg-blue-50 text-blue-700 border-blue-200" };

export default function AdminProfit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState([]);

  const analyze = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai/profit-opportunities");
      setData(data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  const approve = async (rec, i) => {
    try {
      await api.post("/admin/ai/profit-actions", { action: rec.suggested_action });
      setApplied([...applied, i]);
      toast.success("Applied — audited in profit actions");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-profit-page">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white"><TrendingUp className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Profit Optimization</h1>
          <p className="text-sm text-slate-500">Finds margin leaks across your shipping economics. Nothing activates without your approval.</p>
        </div>
      </div>

      {!data && (
        <button onClick={analyze} disabled={loading} data-testid="profit-analyze-btn"
          className="mt-8 flex items-center gap-2 rounded-xl bg-shipany-navy px-8 py-4 font-display text-sm font-bold text-white hover:bg-shipany-blue disabled:opacity-60 glow-blue">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-shipany-cyan" />}
          {loading ? "Analyzing platform economics…" : "Run Profit Analysis"}
        </button>
      )}

      {data && (
        <div className="mt-6 space-y-4" data-testid="profit-results">
          {data.narrative && (
            <div className="rounded-2xl bg-shipany-navy p-5 text-white" data-testid="profit-narrative">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-shipany-cyan"><Sparkles className="h-3.5 w-3.5" /> Analysis summary</p>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{data.narrative}</p>
            </div>
          )}
          {data.recommendations.map((r, i) => (
            <div key={i} className={`rounded-2xl border bg-white p-5 ${SEV_CLS[r.severity] ? "border-slate-200" : ""}`} data-testid={`profit-rec-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${SEV_CLS[r.severity] || ""}`}>{r.severity}</span>
                  <p className="font-display font-bold text-shipany-navy">{r.title}</p>
                </div>
                {applied.includes(i)
                  ? <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Applied</span>
                  : <button onClick={() => approve(r, i)} data-testid={`profit-approve-${i}`} className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">Approve & Apply</button>}
              </div>
              <p className="mt-2 text-sm text-slate-600">{r.detail}</p>
              <p className="mono mt-1 text-[11px] text-slate-400">suggested: {JSON.stringify(r.suggested_action)}</p>
            </div>
          ))}
          {!data.recommendations.length && <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">No opportunities detected — margins look healthy.</div>}
          <button onClick={analyze} data-testid="profit-rerun-btn" className="text-sm font-semibold text-shipany-blue hover:underline">Re-run analysis</button>
        </div>
      )}
    </div>
  );
}
